import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectDitaContent } from "../components/Content/ContentSlice";
import { contentThunk } from "../components/Content/ContentThunk";
import { getGuidelineUpdatesDitamapPath } from "../utils/guidelineAbbreviations";
import { CCMS_BASE_URL } from "../constants/ccms";

/**
 * Custom hook to check if a guideline has DITA updates available 
 * Returns: null (loading), true (has updates), false (no updates)
 */
const useGuidelineDitaUpdates = (guidelineName) => {
  const [hasDitaUpdates, setHasDitaUpdates] = useState(null);
  const content = useSelector(selectDitaContent);
  const dispatch = useDispatch();

  useEffect(() => {
    const checkForDitaUpdates = async () => {
      if (!guidelineName) {
        setHasDitaUpdates(false);
        return;
      }

      const ditamapPath = getGuidelineUpdatesDitamapPath(guidelineName);

      if (!ditamapPath) {
        setHasDitaUpdates(false);
        return;
      }

      // Check if content exists in cache
      const updateContent = content?.content?.find(
        (c) => c.ditafile === ditamapPath,
      );

      if (updateContent) {
        try {
          const parser = new DOMParser();
          const xml = parser.parseFromString(updateContent.content, "text/xml");
          const parseError = xml.querySelector("parsererror");

          if (parseError || !xml.querySelector("bookmap")) {            
            setHasDitaUpdates(false);
            return;
          }

          setHasDitaUpdates(true);
          return;
        } catch {
          setHasDitaUpdates(false);
          return;
        }
      }

      // Try to fetch it directly to check if it exists
      // We do a direct fetch here (instead of using contentThunk) because:
      // 1. We need to validate the HTTP status (404 check) before caching
      // 2. We need to parse and validate the XML structure
      // 3. Only after validation we dispatch contentThunk to cache it
      try {
        const response = await fetch(
          `${CCMS_BASE_URL}/${ditamapPath}`,
        );

        if (!response.ok) {          
          setHasDitaUpdates(false);
          return;
        }

        const text = await response.text();

        // Validate it's a proper bookmap XML
        try {
          const parser = new DOMParser();
          const xml = parser.parseFromString(text, "text/xml");
          const parseError = xml.querySelector("parsererror");

          if (parseError || !xml.querySelector("bookmap")) {            
            setHasDitaUpdates(false);
            return;
          }
          
          dispatch(contentThunk(ditamapPath));
          setHasDitaUpdates(true);
        } catch {
          setHasDitaUpdates(false);
        }
      } catch {
        setHasDitaUpdates(false);
      }
    };

    checkForDitaUpdates();
  }, [guidelineName, content, dispatch]);

  return hasDitaUpdates;
};

export default useGuidelineDitaUpdates;
